import React from 'react';

// "Ledger & Seal" layout, shared with DynamicPricingOnePager and
// AgenticReadinessOnePager. No title/video here -- heroBanner: true
// handles that (see useCases.js).
const PALETTE = {
  blueLight: '#60a5fa',
  blue: '#1d4ed8',
  indigo: '#4f46e5',
  violet: '#6d28d9',
  violetLight: '#a78bfa',
  pink: '#9d174d',
};

// Ends on light purple, not dark pink, per feedback.
const stampGradient = `linear-gradient(120deg, ${PALETTE.blueLight} 0%, ${PALETTE.blue} 35%, ${PALETTE.indigo} 65%, ${PALETTE.violet} 85%, ${PALETTE.violetLight} 100%)`;

// Separate gradient just for the ledger numbers, kept distinct from PALETTE.
const LEDGER_COLORS = ['#60a5fa', '#2563eb', '#4338ca', '#7c3aed', '#9333ea'];

const checklist = [
  { title: 'Automated ingestion & classification', body: 'Customer RFQs and ERP quote extracts sorted automatically on arrival.' },
  { title: 'Scoping questions & methodology preview', body: 'Confirms SKUs, tiers, and assumptions before processing hundreds of rows.' },
  { title: 'Full cost waterfall engine', body: 'Raw material, conversion, tooling, S&A, freight, per item, per tier, per round.' },
  { title: 'Flags gaps instead of guessing', body: "When source data is missing or unclear, it flags it for your team; it never estimates a number on its own." },
  { title: 'Historical pricing reference library', body: 'Grows with every round for faster, more consistent future pricing.' },
  { title: 'Ready-to-submit outputs', body: "Approved pricing transformed directly into the customer's brand and submission format." },
];

const stamps = [
  { num: '01', title: 'Tools you use', body: 'SharePoint, OneDrive, Google Drive, ERP, CRM' },
  { num: '02', title: 'One secure space', body: 'Locked to one account, nothing re-typed' },
  { num: '03', title: 'Agent does the work', body: 'Reads files, runs the math, drafts both docs' },
  { num: '04', title: 'Team reviews & sends', body: 'Approve, then the submission is ready' },
];

const ledgerRows = [
  { label: 'Less manual effort per RFP', value: '~80%' },
  { label: 'Per RFP, incl. full human review', value: '100 → 20 hrs' },
  { label: 'Pilot to production-ready', value: '4-6 weeks' },
  { label: 'Human approval on final pricing', value: '100%' },
];

const RFPResponseOnePager = () => {
  return (
    // Redesign pass for legibility/spacing on DESKTOP: mobile keeps its
    // original, more generous spacing (unprefixed classes below), and sm:
    // and up now tighten instead -- smaller side padding, smaller vertical
    // gaps between sections, so the page reads as one connected write-up
    // on a wide screen instead of a series of far-apart blocks.
    <div className="w-full rounded-2xl bg-[#faf6ee] p-4 sm:p-5">
      <div
        className="relative mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-6 pl-7 shadow-sm sm:mb-6 sm:p-5 sm:pl-6"
        style={{ borderLeft: `5px solid ${PALETTE.blue}` }}
      >
        <div className="mb-4 text-xs font-bold uppercase tracking-widest" style={{ color: PALETTE.indigo }}>
          Business Case Summary
        </div>

        <div className="mb-4">
          <h4 className="text-xl text-gray-900">The Challenge</h4>
          <p className="mt-1 text-base italic text-gray-500">Slow, manual, and easy to get wrong at scale.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            Every pricing round means manually reconciling a customer bid template, an ERP cost export, and freight and constraint files, then rebuilding the full cost waterfall by hand raw material, conversion, tooling, S&A, freight per item, per tier, per round, sometimes across 500+ SKU rows and dozens of brand accounts. Two deliverables, a customer-facing bid form and an internal executive report-out, must tie out exactly every time.
          </p>
        </div>

        <hr className="my-4 border-dashed border-[#e7dcc8]" />

        <div>
          <h4 className="text-xl text-gray-900">The Outcome</h4>
          <p className="mt-1 text-base italic text-gray-500">Agents automate quantitative analysis based on direct rule.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            An AI agent built on Amazon Quick that automates ingestion, pricing calculation, and submission packaging end-to-end while keeping final pricing decisions and approval firmly with the commercial team at every stage. What began as bid-response automation now extends into the analysis that sets pricing strategy for the quarter. Freeing up the team to focus their time to better understand market position and risk factors that only seasoned industry experts would know. 
          </p>
        </div>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900 sm:mb-3">What the agent does</h3>
      <div className="mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8] sm:mb-6">
        {checklist.map((item, i) => (
          <div
            key={item.title}
            className={`flex items-start gap-4 px-5 py-4 sm:px-4 sm:py-3 ${i !== checklist.length - 1 ? 'border-b border-[#f1e9d8]' : ''}`}
          >
            <svg
              className="mt-1.5 h-5 w-5 shrink-0"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke={PALETTE.indigo}
              strokeWidth="3"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{item.title}</h4>
              <p className="mt-1 text-base text-gray-500">{item.body}</p>
            </div>
          </div>
        ))}
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900 sm:mb-3">
        Designed for business, not coders
      </h3>
      <div className="relative mb-8 rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-7 sm:mb-6 sm:p-5">
        <div
          className="absolute left-[6%] right-[6%] top-[51px] hidden h-[2px] sm:block"
          style={{ background: stampGradient, opacity: 0.35 }}
          aria-hidden="true"
        />
        <div className="relative grid grid-cols-1 gap-6 sm:grid-cols-4">
          {stamps.map((stamp, i) => (
            <div key={stamp.num} className="text-center">
              <div
                className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full text-xl font-extrabold text-white"
                style={{
                  background: `linear-gradient(135deg, ${
                    [PALETTE.blueLight, PALETTE.blue, PALETTE.indigo, PALETTE.violet][i]
                  }, ${[PALETTE.blue, PALETTE.indigo, PALETTE.violet, PALETTE.violetLight][i]})`,
                }}
              >
                {stamp.num}
              </div>
              <h4 className="text-lg font-semibold text-gray-900">{stamp.title}</h4>
              <p className="mt-1 text-base text-gray-500">{stamp.body}</p>
            </div>
          ))}
        </div>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900 sm:mb-3">
        Business Value Created
      </h3>
      <div className="grid grid-cols-1 rounded-md border border-[#e7dcc8] bg-[#fffdf8] sm:grid-cols-4">
        {ledgerRows.map((row, i) => (
          <div
            key={row.label}
            className={`flex flex-col items-center gap-1 px-4 py-5 text-center ${
              i !== ledgerRows.length - 1 ? 'border-b border-[#f1e9d8] sm:border-b-0 sm:border-r' : ''
            }`}
          >
            <span
              className="text-4xl font-extrabold"
              style={{
                background: `linear-gradient(135deg, ${LEDGER_COLORS[i]}, ${LEDGER_COLORS[i + 1]})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {row.value}
            </span>
            <span className="text-base font-semibold text-gray-600">{row.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RFPResponseOnePager;
